import Images from 'app/assets/img';

export const FHT_FALLBACK_IMAGE = Images.ImgBanner;

export type DrawerFHTFlightSummary = {
  /** Fallback khi chưa có object sân bay */
  routeLabel?: string;
  departureAirport?: {
    code?: string;
    name?: string;
    cityName?: string;
    countryName?: string;
  };
  destinationAirport?: {
    code?: string;
    name?: string;
    cityName?: string;
    countryName?: string;
  };
  stage?: 'one-way' | 'round-trip';
  dateLabel: string;
  nights: number;
};

export type FhtRoomOffer = {
  key: string;
  roomType: any;
  pricing: any;
  paxPrice: number;
  tickets: any[];
  tags: string[];
  raw: any;
};

export type FhtOfferOverride = {
  tickets: any[];
  paxPrice: number;
  selectedTicket: any;
};

export const calculateOfferPaxPrice = (
  pricing: any,
  selectedTicket?: any,
): number => {
  const basePaxPrice = Number(pricing?.paxPrice || 0);
  const ticketAdditional = Number(selectedTicket?.paxAdditionalAmount ?? 0);
  return basePaxPrice + ticketAdditional;
};

export const buildResolvedRoomOffer = (
  offer: FhtRoomOffer,
  offerOverride?: FhtOfferOverride,
) => {
  if (!offerOverride) return offer.raw;
  return {
    ...offer.raw,
    pricing: {
      ...offer.pricing,
      paxAdditionalAmount: Number(
        offerOverride.selectedTicket?.paxAdditionalAmount ?? 0,
      ),
      tickets: offerOverride.tickets,
    },
  };
};

export type FhtPolicyRow = {
  label: string;
  value: string;
  variant?: 'checkInOut';
};

export const getStarCount = (hotel: any): number => {
  const raw =
    hotel?.starRating ??
    hotel?.rating ??
    hotel?.star ??
    hotel?.hotelStar ??
    0;
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.min(5, Math.round(n));
};

export const normalizeRoomOffers = (hotelData: any): FhtRoomOffer[] => {
  const payload = hotelData?.data ? hotelData.data : hotelData;
  const roomTypesRaw = payload?.roomTypes;
  const roomTypes = Array.isArray(roomTypesRaw)
    ? roomTypesRaw
    : roomTypesRaw
      ? [roomTypesRaw]
      : [];
  if (!roomTypes.length) return [];

  return roomTypes.map((entry, index) => {
    const roomType = entry?.roomType || {};
    const pricing = entry?.pricing || {};
    const tickets = Array.isArray(pricing?.tickets) ? pricing.tickets : [];
    const paxPrice = calculateOfferPaxPrice(pricing, tickets[0]);
    const tags: string[] = [];

    if (pricing?.isRefundable === false || entry?.nonRefundable) {
      tags.push('non_refundable');
    }
    (roomType?.amenities || []).forEach((a: any) => {
      if (a?.name) tags.push(a.name);
    });
    (pricing?.tags || []).forEach((t: string) => tags.push(t));

    return {
      key: String(roomType?.id ?? roomType?.code ?? index),
      roomType,
      pricing,
      paxPrice,
      tickets,
      tags,
      raw: entry,
    };
  });
};

export const groupRoomOffersByType = (
  offers: FhtRoomOffer[],
): { groupTitle: string; offers: FhtRoomOffer[] }[] => {
  const map = new Map<string, { groupTitle: string; offers: FhtRoomOffer[] }>();

  offers.forEach(offer => {
    const title = offer.roomType?.name || '';
    const groupKey = String(offer.roomType?.id ?? title);
    if (!map.has(groupKey)) {
      map.set(groupKey, { groupTitle: title, offers: [] });
    }
    map.get(groupKey)!.offers.push(offer);
  });

  return Array.from(map.values());
};

const policyText = (value: unknown): string => {
  if (value == null || value === '') return '';
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) return value.filter(Boolean).join('\n');
  if (typeof value === 'object') {
    return Object.values(value as Record<string, unknown>)
      .filter(Boolean)
      .map(v => String(v))
      .join('\n');
  }
  return String(value);
};

const formatTimeDisplay = (time?: string): string => {
  if (!time) return '';
  const parts = time.split(':');
  if (parts.length >= 2) {
    const hh = parts[0].padStart(2, '0');
    const mm = parts[1].padStart(2, '0').slice(0, 2);
    return `${hh}:${mm}`;
  }
  return time;
};

const normalizePolicyTimeValue = (
  raw?: string,
  dashStyle: 'spaced' | 'compact' = 'spaced',
): string => {
  if (!raw?.trim()) return '';
  const trimmed = raw.trim();
  const parts = trimmed
    .split(/\s*[-–—]\s*/)
    .map(part => formatTimeDisplay(part))
    .filter(Boolean);

  if (parts.length >= 2) {
    const dash = dashStyle === 'spaced' ? ' – ' : '–';
    return parts.join(dash);
  }

  return formatTimeDisplay(trimmed);
};

export const buildCheckInOutPolicyDisplay = (
  hotel: any,
  translations?: Record<string, string>,
): string => {
  const lines: string[] = [];
  const checkIn = normalizePolicyTimeValue(hotel?.checkInTime, 'spaced');
  const checkOut = normalizePolicyTimeValue(hotel?.checkOutTime, 'compact');
  const receptionLabel =
    translations?.['b2b.txt.frontDeskHours'];

  if (checkIn) {
    lines.push(
      `${translations?.['b2b.txt.checkIn']}: ${checkIn}`,
    );
  }
  if (checkOut) {
    lines.push(
      `${translations?.['b2b.txt.checkOut']}: ${checkOut}`,
    );
  }
  lines.push(`${receptionLabel}: 24/7`);

  return lines.join('\n');
};

export const buildHotelPolicies = (
  hotel: any,
  translations?: Record<string, string>,
): FhtPolicyRow[] => {
  const rows: FhtPolicyRow[] = [
    {
      label:
        translations?.['b2b.txt.checkInCheckOutTime'] || '',
      value: buildCheckInOutPolicyDisplay(hotel, translations),
      variant: 'checkInOut',
    },
    {
      label:
        translations?.['b2b.txt.childPolicy'] || '',
      value: policyText(hotel?.childPolicy),
    },
    {
      label:
        translations?.['b2b.txt.cribExtraBedPolicy'] ||
        '',
      value: policyText(
        hotel?.extraBedPolicy,
      ),
    },
    {
      label: translations?.['b2b.txt.breakfast'] || '',
      value: policyText(hotel?.breakfastPolicy),
    },
    {
      label: translations?.['b2b.txt.pets'] || '',
      value: policyText(hotel?.petPolicy),
    },
    {
      label:
        translations?.['b2b.txt.generalAmenities'] || '',
      value: policyText(
          (hotel?.amenities || [])
            .map((a: any) => a?.name)
            .filter(Boolean)
            .join(', '),
      ),
    },
    {
      label:
        translations?.['b2b.txt.additionalPolicies'] ||
        'Chính sách bổ sung',
      value: policyText(
          hotel?.termsAndConditions,
      ),
    },
  ];

  return rows.filter(row => row.value.trim().length > 0);
};

export const getHotelAmenityTags = (hotel: any): string[] => {
  const list = hotel?.amenities || hotel?.facilities || [];
  if (!Array.isArray(list)) return [];
  return list
    .map((a: any) => (typeof a === 'string' ? a : a?.name))
    .filter(Boolean);
};

/** API có thể trả hotel ở root hoặc thiếu roomTypes — dùng để quyết định fallback sample */
export const hasDrawerHotelContent = (hotelData: any): boolean => {
  if (!hotelData) return false;
  const payload = hotelData?.data ? hotelData.data : hotelData;
  const roomTypesRaw = payload?.roomTypes;
  if (Array.isArray(roomTypesRaw) && roomTypesRaw.length > 0) return true;
  if (roomTypesRaw && typeof roomTypesRaw === 'object') return true;
  return false;
};

export const resolveDrawerHotelData = (hotelData: any): any => {
  const payload = hotelData?.data ? hotelData.data : hotelData;
  if (hasDrawerHotelContent(payload)) {
    const hotel = payload.hotel ?? payload;
    return {
      ...payload,
      hotel: payload.hotel ?? {
        id: hotel.id,
        name: hotel.name ?? hotel.hotelName,
        address: hotel.address,
        images: hotel.images,
        amenities: hotel.amenities,
        policies: hotel.policies,
        starRating: hotel.starRating ?? hotel.rating,
        distanceFromCenter: hotel.distanceFromCenter ?? hotel.distance,
      },
      roomTypes: payload.roomTypes,
    };
  }
  return null;
};
